import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';

const ACCENT = '#00ff88';
const BLUE   = '#00aaff';

const STEPS = [
  {
    num: 1,
    icon: '🌐',
    titleKey: 'alpaca_onboarding.step1_title',
    titleFallback: 'Crea il tuo account Alpaca',
    descKey: 'alpaca_onboarding.step1_desc',
    descFallback: 'Vai su alpaca.markets e registrati gratuitamente. Scegli "Individual Account" come tipo di conto e completa la registrazione base.',
    tag: 'alpaca.markets',
    tagColor: BLUE,
  },
  {
    num: 2,
    icon: '🪪',
    titleKey: 'alpaca_onboarding.step2_title',
    titleFallback: 'Verifica la tua identità',
    descKey: 'alpaca_onboarding.step2_desc',
    descFallback: 'Alpaca richiede una verifica KYC. Carica un documento d\'identità valido e attendi l\'approvazione (di solito entro pochi minuti).',
    tag: 'Verifica KYC',
    tagColor: '#ffaa00',
  },
  {
    num: 3,
    icon: '💰',
    titleKey: 'alpaca_onboarding.step3_title',
    titleFallback: 'Deposita i fondi',
    descKey: 'alpaca_onboarding.step3_desc',
    descFallback: 'Una volta approvato, effettua un bonifico bancario verso il tuo conto Alpaca. I fondi saranno disponibili in 1-3 giorni lavorativi.',
    tag: 'ACH / Wire Transfer',
    tagColor: ACCENT,
  },
  {
    num: 4,
    icon: '🔑',
    titleKey: 'alpaca_onboarding.step4_title',
    titleFallback: 'Genera le API Key',
    descKey: 'alpaca_onboarding.step4_desc',
    descFallback: 'Dalla dashboard Alpaca vai su "Overview" → "API Keys" → "Generate New Key". Tieni pronta la tua API Key e la Secret Key da inserire su Kuesto.',
    tag: 'Overview → API Keys',
    tagColor: '#cc88ff',
  },
];

export default function AlpacaOnboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div style={{
      backgroundColor: SEMANTIC.backgroundApp,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: UI.spacing.lg,
    }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', width: '100%' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto Logo" style={{ width: '120px' }} />
        </div>

        <div style={{
          backgroundColor: SEMANTIC.backgroundCard,
          borderRadius: UI.borderRadius.md,
          padding: UI.spacing.xl,
          boxShadow: UI.shadow.card,
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🦙</div>
            <h2 style={{
              color: BLUE,
              fontSize: UI.fontSize.title,
              fontWeight: UI.fontWeight.bold,
              marginBottom: 6,
            }}>
              {t('alpaca_onboarding.title', 'Come aprire un conto Alpaca')}
            </h2>
            <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.md, lineHeight: 1.5, maxWidth: 460, margin: '0 auto' }}>
              {t('alpaca_onboarding.subtitle', 'Per usare il Real Trading su Kuesto hai bisogno di un account Alpaca personale. Segui questi passaggi:')}
            </p>
          </div>

          {/* Step cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
            {STEPS.map((step) => (
              <div key={step.num} style={{
                display: 'flex',
                gap: 16,
                padding: '16px 18px',
                backgroundColor: SEMANTIC.backgroundApp,
                borderRadius: UI.borderRadius.sm,
                border: '1px solid rgba(255,255,255,0.06)',
              }}>
                {/* Number bubble */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: `${step.tagColor}22`,
                  border: `2px solid ${step.tagColor}66`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: step.tagColor, fontWeight: 700, fontSize: 15,
                }}>
                  {step.num}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18 }}>{step.icon}</span>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                      {t(step.titleKey, step.titleFallback)}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: step.tagColor,
                      backgroundColor: `${step.tagColor}18`,
                      border: `1px solid ${step.tagColor}44`,
                      padding: '2px 7px', borderRadius: 20,
                      letterSpacing: 0.3,
                    }}>
                      {step.tag}
                    </span>
                  </div>
                  <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                    {t(step.descKey, step.descFallback)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Info box */}
          <div style={{
            backgroundColor: 'rgba(0,170,255,0.08)',
            border: '1px solid rgba(0,170,255,0.25)',
            borderRadius: UI.borderRadius.sm,
            padding: '12px 16px',
            marginBottom: 28,
            fontSize: 12,
            color: SEMANTIC.textSecondary,
            lineHeight: 1.6,
          }}>
            ℹ️ <strong style={{ color: BLUE }}>
              {t('alpaca_onboarding.info_title', 'Nota:')}{' '}
            </strong>
            {t('alpaca_onboarding.info_body', 'Le tue credenziali Alpaca sono personali e vengono memorizzate in modo sicuro su Kuesto. Potrai inserirle nella schermata successiva non appena le avrai generate dal portale Alpaca.')}
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/select-portfolio-type')}
            style={{
              width: '100%',
              padding: UI.spacing.md,
              backgroundColor: BLUE,
              color: '#000',
              border: 'none',
              borderRadius: UI.borderRadius.sm,
              fontSize: UI.fontSize.lg,
              fontWeight: UI.fontWeight.bold,
              cursor: 'pointer',
            }}
          >
            {t('alpaca_onboarding.cta', 'Ho capito, vai alla home →')}
          </button>
        </div>
      </div>
    </div>
  );
}
